// eslint-disable-next-line
import React, {
  useRef,
  useState,
  useLayoutEffect,
  useEffect,
  useCallback
} from 'react';
import ContentEditable from 'react-contenteditable';
import './EditableTableColumn.scss';

import { useShortKeysNavigation } from '../../../hooks/useShortKeysNavigation';

const TABLE_HEADER_HEIGHT = 40;
const TABLE_ROW_HEIGHT = 36;
const HEADER_BODY_MARGIN = 4;

// Мінімальна ширина (у пікселях) для кожного поля,
// щоб текст не стискався до 0. Можна змінити за потреби.
const MIN_FIELD_WIDTH = 1;

const EditableTableColumn = ({
  tableX = 0,
  tableY = 0,
  tableWidth = 0,
  scale = 1,
  originXY = { x: 0, y: 0 },

  elementId,
  insideElementId,
  insideElementIndex,

  name,
  type,

  isAutoIncrement,
  isEnum,
  isNN,
  isDefaultValue,
  isGeneratedValue,
  isPK,
  isFK,

  // Колбеки для оновлення тексту
  handleNameChange,
  handleTypeChange,


  handleFocusOnColumnBelow,
  handleFocusOnColumnAbove,

  handleAddNewColumnBelow,

  handleMoveColumnBelow,
  handleMoveColumnAbove,

  handleDeleteColumn,

  otherRowsMaxWidth = 250,
}) => {
  const { isMac } = useShortKeysNavigation();

  const fieldNameRef = useRef(null);
  const fieldTypeRef = useRef(null);

  const focusedFieldRef = useRef(null);
  const insideElementIdRef = useRef(null);
  const elementIdRef = useRef(null);

  const onClickNameField = () => {
    const isFieldNameFocused = fieldNameRef.current === document.activeElement;
    if (isFieldNameFocused && focusedFieldRef.current !== 'name') {
      focusedFieldRef.current = 'name';
      focusNameField();
    }
  }
  const onClickTypeField = () => {
    const isFieldTypeFocused = fieldTypeRef.current === document.activeElement;
    if (isFieldTypeFocused && focusedFieldRef.current !== 'type') {
      focusedFieldRef.current = 'type';
      focusTypeField();
    }
  }

  const focusNameField = () => {
    if (fieldNameRef.current) {
      // Автофокус на елементі
      fieldNameRef.current.focus();

      // Виділення всього тексту
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(fieldNameRef.current);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }
  const focusTypeField = () => {
    if (fieldTypeRef.current) {
      // Автофокус на елементі
      fieldTypeRef.current.focus();

      // Виділення всього тексту
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(fieldTypeRef.current);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }



  const handleKeyDown = (event, field) => {
    const isCtrl = isMac ? event.metaKey : event.ctrlKey;

    // Ignore events from disabled or read-only elements
    if (event.target.disabled || event.target.readOnly) return;

    // -----------------------------------------------------------------------------
    // + [TAB] = [FOCUS ON OTHER FIELD]
    if (event.code === 'Tab' && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();

      if (field == 'name') {
        focusedFieldRef.current = 'type';
        focusTypeField();
      } else {
        focusedFieldRef.current = 'name';
        focusNameField();
      }
    }

    // -----------------------------------------------------------------------------
    // + [ENTER] = [FOCUS ON COLUMN BELOW / ADD NEW COLUMN BELOW]
    else if ((event.code === 'Enter' || event.code === 'NumpadEnter') &&
      !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      handleAddNewColumnBelow(elementIdRef.current, insideElementIdRef.current, true);
    }
    // + [CTRL + ENTER] = [ADD NEW COLUMN BELOW]
    else if ((event.code === 'Enter' || event.code === 'NumpadEnter') &&
      isCtrl && !event.altKey) {
      event.preventDefault();
      handleAddNewColumnBelow(elementIdRef.current, insideElementIdRef.current, false);
    }

    // -----------------------------------------------------------------------------
    // + [CTRL + DOWN] = [FOCUS ON COLUMN BELOW]
    else if (event.code === 'ArrowDown' &&
      isCtrl && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      handleFocusOnColumnBelow(elementIdRef.current, insideElementIdRef.current);
    }
    // + [CTRL + UP] = [FOCUS ON COLUMN ABOVE]
    else if (event.code === 'ArrowUp' &&
      isCtrl && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      handleFocusOnColumnAbove(elementIdRef.current, insideElementIdRef.current);
    }

    // -----------------------------------------------------------------------------
    // + [CTRL + SHIFT + DOWN] = [MOVE COLUMN BELOW]
    else if (event.code === 'ArrowDown' &&
      isCtrl && event.shiftKey && !event.altKey) {
      event.preventDefault();
      handleMoveColumnBelow(elementIdRef.current, insideElementIdRef.current);
    }
    // + [CTRL + SHIFT + UP] = [MOVE COLUMN ABOVE]
    else if (event.code === 'ArrowUp' &&
      isCtrl && event.shiftKey && !event.altKey) {
      event.preventDefault();
      handleMoveColumnAbove(elementIdRef.current, insideElementIdRef.current);
    }

    // -----------------------------------------------------------------------------
    // + [CTRL + DELETE] = [DELETE COLUMN + FOCUS ON NEXT COLUMN]
    else if (event.code === 'Delete' &&
      isCtrl && !event.shiftKey && !event.altKey) {
      event.preventDefault();
      handleDeleteColumn(elementIdRef.current, insideElementIdRef.current);
    }
    // -----------------------------------------------------------------------------
  }

  // const focusAll = useCallback((elem) => {
  //   if (!elem) return;
  //   elem.focus();
  //   const range = document.createRange();
  //   range.selectNodeContents(elem);
  //   const selection = window.getSelection();
  //   selection.removeAllRanges();
  //   selection.addRange(range);
  // }, []);

  // const onClickNameField = () => {
  //   focusAll(fieldNameRef.current);
  // };
  // const onClickTypeField = () => {
  //   focusAll(fieldTypeRef.current);
  // };






  const isChangedColumnWidthRef = useRef(false);
  const changesCounterRef = useRef(0);

  const measureCanvasRef = useRef(document.createElement('canvas'));

  const [leftWidth, setLeftWidth] = useState(MIN_FIELD_WIDTH);
  const [rightWidth, setRightWidth] = useState(MIN_FIELD_WIDTH);

  const measureText = useCallback((txt, fontSizePx, isBold) => {
    const ctx = measureCanvasRef.current.getContext('2d');
    ctx.font = `${isBold ? 700 : 400} ${fontSizePx}px Inter, sans-serif`;
    return ctx.measureText(txt || '').width + 5;
  }, []);

  const recalcWidths = useCallback(() => {
    console.log('recalcWidths')
    let container = tableWidth * scale - 25 * scale;
    if (container < 0) container = 0;

    let iconsOffset = 28;
    const distanceBetween = 4.5;
    if (isGeneratedValue) iconsOffset += 16 + distanceBetween;
    if (isDefaultValue) iconsOffset += 17 + distanceBetween;
    if (isNN) iconsOffset += 25 + distanceBetween;
    if (isEnum) iconsOffset += 15.25 + distanceBetween;
    if (isAutoIncrement) iconsOffset += 15.5 + distanceBetween;

    const containerWidth = container - iconsOffset * scale;
    // if (containerWidth <= 0) {
    //   setLeftWidth(MIN_FIELD_WIDTH);
    //   setRightWidth(MIN_FIELD_WIDTH);
    //   return;
    // }

    const isBold = isPK || isFK;
    const leftNeeded = measureText(name, 19.2 * scale, isBold);
    const rightNeeded = measureText(type, 18 * scale, false);
    const sumNeeded = leftNeeded + rightNeeded;

    if (sumNeeded + 15 * scale <= containerWidth) {
      console.log('1-')
      // if (sumNeeded + 15 * scale <= containerWidth && sumNeeded + 15 * scale <= otherRowsMaxWidth * scale) {
      const leftover = containerWidth - sumNeeded;

      const half = leftover / 2;
      let newLeft = leftNeeded + half;
      let newRight = rightNeeded + half;

      console.log('1=', newLeft, newRight, leftover)
      if (newLeft < MIN_FIELD_WIDTH) newLeft = MIN_FIELD_WIDTH;
      if (newRight < MIN_FIELD_WIDTH) newRight = MIN_FIELD_WIDTH;

      setLeftWidth(newLeft);
      setRightWidth(newRight);

    } else {
      console.log('2-')
      // 5. Сумарна довжина текстів більше, ніж поточний контейнер => потрібно збільшувати ширину таблиці
      const diffPx = sumNeeded - containerWidth + 20 * scale;
      const diffLogical = diffPx / scale;
      const newTableWidth = tableWidth + diffLogical;

      const ratio = containerWidth / sumNeeded;
      let scaledLeft = leftNeeded * ratio;
      let scaledRight = rightNeeded * ratio;

      if (scaledLeft < MIN_FIELD_WIDTH) {
        scaledLeft = MIN_FIELD_WIDTH;
      }
      if (scaledRight < MIN_FIELD_WIDTH) {
        scaledRight = MIN_FIELD_WIDTH;
      }
      setLeftWidth(scaledLeft);
      setRightWidth(scaledRight);

      return newTableWidth;
    }
  }, [
    name,
    type,
    scale,
    tableWidth,
    isAutoIncrement,
    isEnum,
    isNN,
    isDefaultValue,
    isGeneratedValue,
    measureText
  ]);


  // ---- РОЗРАХУНОК ШИРИН ----
  const customRecalcWidths = (newName, newType) => {
    console.log('customRecalcWidths')

    // 1. Поточна ширина елемента (в пікселях),
    //    з урахуванням відступу зліва (25 * scale).
    //    Якщо tableWidth у вас одразу в пікселях, то просто const container = tableWidth
    let container = tableWidth * scale - 25 * scale;
    if (container < 0) container = 0;

    // 2. Враховуємо іконки праворуч (isDefaultValue, isNN, тощо)
    //    - це базова відстань (28) + іконки
    let iconsOffset = 28;
    const distanceBetween = 5;
    if (isGeneratedValue) iconsOffset += 17 + distanceBetween;
    if (isDefaultValue) iconsOffset += 17 + distanceBetween;
    if (isNN) iconsOffset += 25 + distanceBetween;
    if (isEnum) iconsOffset += 15.25 + distanceBetween;
    if (isAutoIncrement) iconsOffset += 15.5 + distanceBetween;

    const containerWidth = container - iconsOffset * scale;
    // if (containerWidth <= 0) {
    //   // Якщо немає місця — мінімальні значення
    //   setLeftWidth(MIN_FIELD_WIDTH);
    //   setRightWidth(MIN_FIELD_WIDTH);
    //   return;
    // }

    // 3. Вимірюємо, скільки потрібно пікселів для текcтів

    const isBold = isPK || isFK;
    const leftNeeded = measureText(newName, 19.2 * scale, isBold);
    const rightNeeded = measureText(newType, 18 * scale, false);
    const sumNeeded = leftNeeded + rightNeeded;


    // 4. Якщо сумарно тексти влазять у containerWidth
    if (sumNeeded + 15 * scale <= containerWidth) {
      // if (sumNeeded + 15 * scale <= containerWidth && sumNeeded + 15 * scale <= otherRowsMaxWidth * scale) {
      console.log('1-1', tableWidth * scale, containerWidth, sumNeeded)
      // Дивимося, скільки вільного місця залишилося
      const leftover = containerWidth - sumNeeded;

      // Залишок ділимо навпіл і додаємо кожному
      const half = leftover / 2;
      let newLeft = leftNeeded + half;
      let newRight = rightNeeded + half;

      // Тепер перевіримо, щоб кожне поле не було менше MIN_FIELD_WIDTH
      if (newLeft < MIN_FIELD_WIDTH) newLeft = MIN_FIELD_WIDTH;
      if (newRight < MIN_FIELD_WIDTH) newRight = MIN_FIELD_WIDTH;

      setLeftWidth(newLeft);
      setRightWidth(newRight);

    } else {
      console.log('2-2')
      // 5. Сумарна довжина текстів більше, ніж поточний контейнер
      //    => потрібно або масштабувати поля, або збільшувати ширину таблиці
      const diffPx = sumNeeded - containerWidth + 20 * scale;
      // Перетворюємо різницю пікселів у «логічну» (до масштабу)
      const diffLogical = diffPx / scale;
      const newTableWidth = tableWidth + diffLogical;

      // Поки що (доки не оновиться зовнішній tableWidth) робимо пропорційне зменшення
      const ratio = containerWidth / sumNeeded;
      let scaledLeft = leftNeeded * ratio;
      let scaledRight = rightNeeded * ratio;

      // Знову перевірка на MIN_FIELD_WIDTH
      // (хоча логічно, якщо реально бракує місця, усе одно це стискання)
      if (scaledLeft < MIN_FIELD_WIDTH) {
        scaledLeft = MIN_FIELD_WIDTH;
      }
      if (scaledRight < MIN_FIELD_WIDTH) {
        scaledRight = MIN_FIELD_WIDTH;
      }
      setLeftWidth(scaledLeft);
      setRightWidth(scaledRight);

      return newTableWidth;
    }
  }


  // useLayoutEffect(() => {
  //   focusedFieldRef.current = 'name';
  //   focusNameField();
  //   console.log('useLayoutEffect 0')
  // }, []);


  // Використовуємо useLayoutEffect, щоб обчислення і зміна стейту
  // сталася до відмальовування (уникаємо "стрибань")
  useLayoutEffect(() => {
    recalcWidths();
    console.log('useLayoutEffect')
  }, [recalcWidths]);

  // useEffect(() => {
  //   focusedFieldRef.current = 'name';
  //   focusNameField();

  //   console.log('useEffect 0')
  //   return () => { };
  // }, []);


  useEffect(() => {
    focusedFieldRef.current = 'name';
    focusNameField();


    // isChangedColumnWidthRef.current = false;
    changesCounterRef.current = 0;


    if (insideElementId !== insideElementIdRef.current) {
      insideElementIdRef.current = insideElementId;
    }

    if (elementId !== elementIdRef.current) {
      elementIdRef.current = elementId;
    }

    return () => { };
  }, [elementId, insideElementId]);


  useEffect(() => {
    console.log('useEffect L-R', changesCounterRef.current, isChangedColumnWidthRef.current, leftWidth, rightWidth)

    if (leftWidth > 1) {
      isChangedColumnWidthRef.current = true;
      changesCounterRef.current += 1;
    }

    if (isChangedColumnWidthRef.current && changesCounterRef.current == 1) {
      focusedFieldRef.current = 'name';
      focusNameField();
    }

    return () => { };
  }, [leftWidth, rightWidth]);


  // const focusAll = useCallback((elem) => {
  //   if (!elem) return;
  //   elem.focus();
  //   const range = document.createRange();
  //   range.selectNodeContents(elem);
  //   const selection = window.getSelection();
  //   selection.removeAllRanges();
  //   selection.addRange(range);
  // }, []);

  // const onClickNameField = () => {
  //   focusAll(fieldNameRef.current);
  // };
  // const onClickTypeField = () => {
  //   focusAll(fieldTypeRef.current);
  // };

  const sanitizeInput = (val) => (
    (val || '')
      .replace(/<\/?[^>]+(>|$)/g, '')
      .replace(/&[a-zA-Z0-9#]{2,10};/g, '')
      .replace(/\s{2,}/g, ' ')
      .trim()
  );

  const onNameChange = (e) => {
    if (e.nativeEvent.inputType !== 'insertParagraph') {
      const nameValue = sanitizeInput(e.target.value);
      const newWidth = customRecalcWidths(nameValue, type);
      handleNameChange(nameValue, newWidth);
    } else {
      e.nativeEvent.preventDefault();
    }
  };
  const onTypeChange = (e) => {
    if (e.nativeEvent.inputType !== 'insertParagraph') {
      const typeValue = sanitizeInput(e.target.value);
      const newWidth = customRecalcWidths(name, typeValue);
      handleTypeChange(typeValue, newWidth);
    } else {
      e.nativeEvent.preventDefault();
    }
  };

  const columnY = TABLE_HEADER_HEIGHT + HEADER_BODY_MARGIN + insideElementIndex * TABLE_ROW_HEIGHT;

  const columnStyles = {
    position: 'absolute',
    left: tableX * scale + originXY.x + 25 * scale,
    top: (tableY + columnY) * scale + originXY.y,
    width: tableWidth * scale - 25 * scale,
    height: TABLE_ROW_HEIGHT * scale,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  };

  const nameFieldStyles = {
    width: leftWidth,
    outline: 'none',
    textAlign: 'left',
    fontFamily: 'Inter, sans-serif',
    fontSize: 19.2 * scale,
    fontWeight: isPK || isFK ? 700 : 400,
    lineHeight: `${(TABLE_ROW_HEIGHT - 8 - 2) * scale}px`,
    overflow: 'hidden',
    cursor: 'text',
  };
  const typeFieldStyles = {
    width: rightWidth,
    outline: 'none',
    textAlign: 'right',
    fontFamily: 'Inter, sans-serif',
    fontSize: 18 * scale,
    fontWeight: 400,
    lineHeight: `${(TABLE_ROW_HEIGHT - 8 - 2) * scale}px`,
    overflow: 'hidden',
    cursor: 'text'
  };

  return (
    <div className="editable-column" style={columnStyles}>
      <ContentEditable
        innerRef={fieldNameRef}
        style={nameFieldStyles}
        className="editable-column-field name"
        spellCheck="false"
        html={name}
        onChange={onNameChange}
        onKeyDown={(event) => { handleKeyDown(event, 'name'); }}
        onClick={onClickNameField}
      />
      <ContentEditable
        innerRef={fieldTypeRef}
        style={typeFieldStyles}
        className="editable-column-field type"
        spellCheck="false"
        html={type}
        onChange={onTypeChange}
        onKeyDown={(event) => { handleKeyDown(event, 'type'); }}
        onClick={onClickTypeField}
      />
    </div>
  );
};

export default EditableTableColumn;
